import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { errorMessage } from "../api/client";
import {
  slugify,
  useCreateTaskCategory,
  useDeleteTaskCategory,
  useUpdateTaskCategory,
} from "../api/appTasks";
import type { EnvironmentTypeRead, TaskCategoryRead } from "../api/types";
import { Switch, envColor } from "./AppTaskControls";

// "Manage categories" modal: one tab per environment; rename (both languages), recolor and
// (de)activate inline — each row saves on blur/toggle — plus add and delete. Slugs are
// immutable and a category with tasks can only be deactivated, never deleted (409 from the api).

interface Props {
  open: boolean;
  environments: EnvironmentTypeRead[];
  categories: TaskCategoryRead[];
  defaultEnvSlug: string;
  onClose: () => void;
  onToast: (message: string) => void;
}

function CategoryRow({ cat, onToast }: { cat: TaskCategoryRead; onToast: (m: string) => void }) {
  const update = useUpdateTaskCategory();
  const remove = useDeleteTaskCategory();
  const [name, setName] = useState(cat.name);
  const [nameEn, setNameEn] = useState(cat.name_en);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setName(cat.name);
    setNameEn(cat.name_en);
  }, [cat.name, cat.name_en]);

  function commitNames() {
    const body: { name?: string; name_en?: string } = {};
    if (name.trim() && name.trim() !== cat.name) body.name = name.trim();
    if (nameEn.trim() && nameEn.trim() !== cat.name_en) body.name_en = nameEn.trim();
    if (Object.keys(body).length) update.mutate({ id: cat.id, body });
  }

  const busy = update.isPending || remove.isPending;
  const error = update.error ?? remove.error;

  return (
    <div
      className={cat.is_active ? "at-cat-row" : "at-cat-row inactive"}
      style={{ ["--sc" as string]: cat.color ?? "#7c877f" }}
    >
      <label className="at-swatch" title="Badge color">
        <input
          type="color"
          value={cat.color ?? "#7c877f"}
          aria-label={`Color of ${cat.name_en}`}
          disabled={busy}
          onChange={(e) => update.mutate({ id: cat.id, body: { color: e.target.value } })}
        />
      </label>
      <div className="at-cat-names">
        <input
          className="at-cat-input"
          value={name}
          aria-label="Name (pt-BR)"
          disabled={busy}
          onChange={(e) => setName(e.target.value)}
          onBlur={commitNames}
          onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
        />
        <span className="at-slug" title="task_categories.slug — immutable">
          {cat.slug}
        </span>
      </div>
      <input
        className="at-cat-input"
        value={nameEn}
        aria-label="Name (EN)"
        disabled={busy}
        onChange={(e) => setNameEn(e.target.value)}
        onBlur={commitNames}
        onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
      />
      <span className="at-cat-count">
        <b>{cat.task_count}</b> {cat.task_count === 1 ? "task" : "tasks"}
      </span>
      <Switch
        checked={cat.is_active}
        disabled={busy}
        label={`${cat.name_en} active`}
        onChange={(next) =>
          update.mutate(
            { id: cat.id, body: { is_active: next } },
            {
              onSuccess: () =>
                onToast(
                  next
                    ? `“${cat.name_en}” visible in the app again`
                    : `“${cat.name_en}” hidden from the app (its ${cat.task_count} tasks stay linked)`,
                ),
            },
          )
        }
      />
      {confirmDelete ? (
        <span className="at-cat-confirm">
          <button
            className="btn small danger"
            disabled={busy}
            onClick={() =>
              remove.mutate(cat.id, {
                onSuccess: () => onToast(`Category “${cat.name_en}” deleted`),
                onSettled: () => setConfirmDelete(false),
              })
            }
          >
            Confirm
          </button>
          <button className="btn small ghost" disabled={busy} onClick={() => setConfirmDelete(false)}>
            Keep
          </button>
        </span>
      ) : (
        <button
          className="btn small ghost"
          disabled={busy || cat.task_count > 0}
          title={
            cat.task_count > 0
              ? `Has ${cat.task_count} task(s): move them first, or deactivate the category`
              : "Delete this empty category"
          }
          onClick={() => setConfirmDelete(true)}
        >
          Delete
        </button>
      )}
      {error && <div className="error small at-cat-error">{errorMessage(error)}</div>}
    </div>
  );
}

function AddCategoryRow({
  env,
  siblings,
  onToast,
}: {
  env: EnvironmentTypeRead;
  siblings: TaskCategoryRead[];
  onToast: (m: string) => void;
}) {
  const create = useCreateTaskCategory();
  const [name, setName] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [color, setColor] = useState("#64748b");
  const slug = slugify(nameEn);
  const duplicate = slug !== "" && siblings.some((c) => c.slug === slug);
  const ready = name.trim() !== "" && slug !== "" && !duplicate;

  function submit() {
    if (!ready) return;
    create.mutate(
      {
        environment_type_id: env.id,
        slug,
        name: name.trim(),
        name_en: nameEn.trim(),
        color,
        sort_order: siblings.length,
      },
      {
        onSuccess: (saved) => {
          onToast(`Category “${saved.name_en}” added to ${env.name_en}`);
          setName("");
          setNameEn("");
        },
      },
    );
  }

  return (
    <div className="at-cat-add" style={{ ["--sc" as string]: color }}>
      <label className="at-swatch" title="Badge color">
        <input
          type="color"
          value={color}
          aria-label="Color of the new category"
          onChange={(e) => setColor(e.target.value)}
        />
      </label>
      <input
        className="text-input"
        placeholder="New category (pt-BR)"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && submit()}
      />
      <input
        className="text-input"
        placeholder="Name (EN)"
        value={nameEn}
        onChange={(e) => setNameEn(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && submit()}
      />
      <button className="btn small primary" disabled={!ready || create.isPending} onClick={submit}>
        + Add
      </button>
      <div className="at-cat-add-hint">
        {duplicate ? (
          <span className="error small">“{slug}” already exists in {env.name_en}.</span>
        ) : slug ? (
          <span className="muted small">
            slug: <span className="at-slug">{slug}</span>
          </span>
        ) : null}
        {create.isError && <span className="error small"> {errorMessage(create.error)}</span>}
      </div>
    </div>
  );
}

export default function TaskCategoriesDialog({
  open,
  environments,
  categories,
  defaultEnvSlug,
  onClose,
  onToast,
}: Props) {
  const [envSlug, setEnvSlug] = useState(defaultEnvSlug);
  useEffect(() => {
    if (open) setEnvSlug(defaultEnvSlug);
  }, [open, defaultEnvSlug]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  const env = environments.find((e) => e.slug === envSlug) ?? environments[0];
  const rows = categories.filter((c) => c.environment_slug === env?.slug);

  return createPortal(
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal at-modal" role="dialog" aria-modal="true" aria-label="Manage categories">
        <div className="at-modal-head">
          <div>
            <h3 className="modal-title">Manage categories</h3>
            <p className="muted small">
              Each category belongs to one environment. Tasks point at it by id; the slug is what
              the app receives and is fixed once created.
            </p>
          </div>
          <button className="drawer-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="at-modal-body">
          <div className="at-env-tabs" role="tablist" aria-label="Environment">
            {environments.map((e) => (
              <button
                key={e.id}
                role="tab"
                aria-selected={e.slug === env?.slug}
                style={{ ["--sc" as string]: envColor(e.slug) }}
                onClick={() => setEnvSlug(e.slug)}
              >
                {e.name_en}
                <span className="at-cnt">
                  {categories.filter((c) => c.environment_slug === e.slug).length}
                </span>
              </button>
            ))}
          </div>

          <div className="at-cat-list">
            <div className="at-cat-row head">
              <span>Color</span>
              <span>Name (pt-BR, app)</span>
              <span>Name (EN, metadata)</span>
              <span className="at-cat-count">Tasks</span>
              <span>Active</span>
              <span />
            </div>
            {rows.length === 0 && (
              <div className="at-cat-empty muted small">No categories in this environment yet.</div>
            )}
            {rows.map((c) => (
              <CategoryRow key={c.id} cat={c} onToast={onToast} />
            ))}
            {env && <AddCategoryRow env={env} siblings={rows} onToast={onToast} />}
          </div>

          <div className="at-notice info">
            A category with tasks cannot be deleted, only <b>deactivated</b>. Deactivating hides
            the whole group from the app; its tasks stay linked and can be moved.
          </div>
        </div>

        <div className="modal-actions">
          <button className="btn primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
